// =====================================================
// utils/torneos.ts
// Utilidades para Torneos - Multi-Deporte
// =====================================================

import { calcularEstadisticas } from './resultados';

export type Deporte = "Tenis" | "Padel" | "Futbol";

// ---------------------------------------------------------
// Utilidades generales
// ---------------------------------------------------------
export function nextPowerOf2(n: number): number {
  return Math.pow(2, Math.ceil(Math.log2(Math.max(1, n))));
}

// ---------------------------------------------------------
// Interfaz de Partido
// ---------------------------------------------------------
export interface Partido {
  id: number;
  ronda: number;
  local: string | Equipo | null;
  visitante: string | Equipo | null;
  pasaAutomatico: boolean;
  ganador?: string | Equipo | null;
  dePartido?: [number, number];
  detalles?: DetallesPartido;
  sets?: Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
}

export interface Equipo {
  id: number;
  nombre: string;
  ids?: {
    idJugador1: number;
    idJugador2?: number;
  };
}

export interface DetallesPartido {
  fecha?: string;
  hora?: string;
  lugar?: string;
  observaciones?: string;
}

// ---------------------------------------------------------
// Interfaz de Ronda
// ---------------------------------------------------------
export interface Ronda {
  numero: number;
  partidos: Partido[];
}

export type EstrategiaByes = "orden_zonas" | "mejor_clasificacion";

// ---------------------------------------------------------
// Interfaz de Resultado de Torneo
// ---------------------------------------------------------
export interface ResultadoTorneo {
  formato: "eliminacion_directa" | "zonas_y_eliminacion";
  total: number;
  cuadroFinal: number;
  byes: number;
  fases: number;
  rondas: Ronda[];
  zonas?: number;
  participantesPorZona?: number[];
  clasificadosPorZona?: number[];
  totalClasificados?: number;
  criterioClasificacion?: string;
  estrategiaByes?: EstrategiaByes;
  zonasDeseadas?: number;
  deporte?: Deporte;
}

// ---------------------------------------------------------
// NUEVA: Crear sets iniciales según deporte
// ---------------------------------------------------------
function crearSetsIniciales(deporte: Deporte): Array<{ gamesLocal: number | null; gamesVisitante: number | null }> {
  if (deporte === 'Futbol') {
    return [{ gamesLocal: null, gamesVisitante: null }];
  } else {
    return [
      { gamesLocal: null, gamesVisitante: null },
      { gamesLocal: null, gamesVisitante: null },
    ];
  }
}

// ---------------------------------------------------------
// Generar partidos de zona
// ---------------------------------------------------------
export function generarPartidosDeZona(
  equipos: any[],
  deporte: Deporte = 'Tenis'
) {
  const partidos = [];
  let partidoId = 1;

  for (let i = 0; i < equipos.length; i++) {
    for (let j = i + 1; j < equipos.length; j++) {
      partidos.push({
        id: partidoId++,
        local: equipos[i],
        visitante: equipos[j],
        sets: crearSetsIniciales(deporte),
        jugado: false,
        detalles: {}
      });
    }
  }

  return partidos;
}

// ---------------------------------------------------------
// Calcular tabla de zona
// ---------------------------------------------------------
export function calcularTablaZona(zona: any, deporte: Deporte = 'Tenis') {
  const tabla = zona.equipos.map((e: any) => ({
    equipo: e,
    pj: 0,
    pg: 0,
    pe: 0,
    pp: 0,
    setsGanados: 0,
    setsPerdidos: 0,
    setsDiff: 0,
    gamesGanados: 0,
    gamesPerdidos: 0,
    gamesDiff: 0,
    puntos: 0
  }));

  const find = (id: number) => tabla.find((t: any) => t.equipo.id === id);

  zona.partidos.forEach((p: any) => {
    if (!p.sets || p.sets.length === 0) return;
    
    const minimosRequeridos = deporte === 'Futbol' ? 1 : 2;
    if (p.sets.length < minimosRequeridos) return;
    
    const todosLosSetsTienenResultado = p.sets.every(
      (set: any) => 
        set.gamesLocal !== null && 
        set.gamesLocal !== undefined &&
        set.gamesVisitante !== null && 
        set.gamesVisitante !== undefined
    );
    
    if (!todosLosSetsTienenResultado) return;

    const l = find(p.local.id);
    const v = find(p.visitante.id);
    
    if (!l || !v) return;

    // Usar función centralizada
    const statsLocal = calcularEstadisticas([p], p.local.id, deporte);
    const statsVisitante = calcularEstadisticas([p], p.visitante.id, deporte);

    // Acumular estadísticas
    l.pj += statsLocal.pj;
    l.pg += statsLocal.pg;
    l.pe += statsLocal.pe;
    l.pp += statsLocal.pp;
    l.setsGanados += statsLocal.setsGanados;
    l.setsPerdidos += statsLocal.setsPerdidos;
    l.gamesGanados += statsLocal.gamesGanados;
    l.gamesPerdidos += statsLocal.gamesPerdidos;
    l.puntos += statsLocal.puntos;
    
    v.pj += statsVisitante.pj;
    v.pg += statsVisitante.pg;
    v.pe += statsVisitante.pe;
    v.pp += statsVisitante.pp;
    v.setsGanados += statsVisitante.setsGanados;
    v.setsPerdidos += statsVisitante.setsPerdidos;
    v.gamesGanados += statsVisitante.gamesGanados;
    v.gamesPerdidos += statsVisitante.gamesPerdidos;
    v.puntos += statsVisitante.puntos;

    // Calcular diferencias
    l.setsDiff = l.setsGanados - l.setsPerdidos;
    l.gamesDiff = l.gamesGanados - l.gamesPerdidos;
    v.setsDiff = v.setsGanados - v.setsPerdidos;
    v.gamesDiff = v.gamesGanados - v.gamesPerdidos;
  });

  // Ordenar según deporte
  return tabla.sort((a: any, b: any) => {
    if (b.puntos !== a.puntos) return b.puntos - a.puntos;
    
    if (deporte === 'Futbol') {
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    } else {
      if (b.setsDiff !== a.setsDiff) return b.setsDiff - a.setsDiff;
      if (b.setsGanados !== a.setsGanados) return b.setsGanados - a.setsGanados;
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    }
    
    return a.equipo.nombre.localeCompare(b.equipo.nombre);
  });
}

// ---------------------------------------------------------
// Generador de Zonas + Eliminación
// ---------------------------------------------------------
export function generarZonasYEliminacion(
  totalParticipantes: number,
  totalClasificadosDeseados: number,
  zonasDeseadas?: number,
  estrategiaByes: EstrategiaByes = "orden_zonas",
  deporte: Deporte = 'Tenis'
): ResultadoTorneo | null {

  if (totalClasificadosDeseados < 1) throw new Error("totalClasificadosDeseados debe ser >= 1");
  if (totalClasificadosDeseados > totalParticipantes)
    throw new Error("No puede haber más clasificados que participantes.");

  let zonaMin, zonaMax;
  
  if (zonasDeseadas) {
    zonaMin = zonasDeseadas;
    zonaMax = zonasDeseadas;
    
    if (zonasDeseadas > totalParticipantes) {
      throw new Error(`No se pueden crear ${zonasDeseadas} zonas con solo ${totalParticipantes} equipos`);
    }
  } else {
    zonaMin = 2;
    zonaMax = Math.min(8, Math.floor(totalParticipantes / 2));
  }

  let mejorSolucion: ResultadoTorneo | null = null;
  let mejorScore = Infinity;

  for (let zonas = zonaMin; zonas <= zonaMax; zonas++) {

    const base = Math.floor(totalParticipantes / zonas);
    const extras = totalParticipantes % zonas;
    const participantesPorZona = Array(zonas)
      .fill(base)
      .map((v, i) => (i < extras ? v + 1 : v));

    if (participantesPorZona.some(p => p < 2)) {
      continue;
    }

    const clasificadosPorZona = Array(zonas).fill(1);
    let suma = clasificadosPorZona.reduce((a, b) => a + b, 0);
    let cuposRestantes = totalClasificadosDeseados - suma;

    if (cuposRestantes > 0) {
      const idxs = participantesPorZona
        .map((p, i) => ({ p, i }))
        .sort((a, b) => b.p - a.p)
        .map(x => x.i);

      let nivel = 2;
      while (cuposRestantes > 0) {
        for (const i of idxs) {
          if (clasificadosPorZona[i] < participantesPorZona[i]) {
            clasificadosPorZona[i]++;
            cuposRestantes--;
            if (cuposRestantes === 0) break;
          }
        }
        nivel++;
        if (nivel > 6) break;
      }
    }

    suma = clasificadosPorZona.reduce((a, b) => a + b, 0);
    if (suma !== totalClasificadosDeseados) continue;

    const cuadroFinal = nextPowerOf2(totalClasificadosDeseados);
    const byes = cuadroFinal - totalClasificadosDeseados;
    const fases = Math.log2(cuadroFinal);

    const avgParticipantes = totalParticipantes / zonas;
    const desviacion = Math.sqrt(
      participantesPorZona.reduce((sum, p) => sum + Math.pow(p - avgParticipantes, 2), 0) / zonas
    );
    
    const score = desviacion;

    const primeraRonda = generarCrucesConByes(
      clasificadosPorZona, 
      totalClasificadosDeseados,
      estrategiaByes
    );

    const rondas: Ronda[] = [ { numero: 1, partidos: primeraRonda.enfrentamientos } ];
    let rondaActual = primeraRonda.enfrentamientos;
    let idPartido = primeraRonda.enfrentamientos.length + 1;

    while (rondaActual.length > 1) {
      rondaActual = avanzarRondaConIds(rondaActual, idPartido);
      rondas.push({ numero: rondas.length + 1, partidos: rondaActual });
      idPartido += rondaActual.length;
    }

    const solucion: ResultadoTorneo = {
      formato: "zonas_y_eliminacion",
      total: totalParticipantes,
      zonas,
      participantesPorZona,
      clasificadosPorZona,
      totalClasificados: totalClasificadosDeseados,
      cuadroFinal,
      byes,
      fases,
      criterioClasificacion: generarDescripcionClasificacionAvanzado(clasificadosPorZona),
      rondas,
      estrategiaByes,
      zonasDeseadas: zonasDeseadas ? zonas : undefined,
      deporte
    };
    
    if (score < mejorScore) {
      mejorScore = score;
      mejorSolucion = solucion;
    }
  }

  return mejorSolucion;
}

// ---------------------------------------------------------
// Descripción de clasificación
// ---------------------------------------------------------
function generarDescripcionClasificacionAvanzado(clasificadosPorZona: number[]): string {
  const minClas = Math.min(...clasificadosPorZona);
  const maxClas = Math.max(...clasificadosPorZona);

  if (minClas === maxClas) {
    return `Los ${minClas} primeros de cada zona`;
  }

  const extras = clasificadosPorZona.filter(c => c > minClas).length;
  const nivelExtra = minClas + 1;

  if (extras === 1) {
    return `Los ${minClas} primeros de cada zona y el mejor ${nivelExtra}º`;
  }

  return `Los ${minClas} primeros de cada zona y los ${extras} mejores ${nivelExtra}ºs`;
}

// ---------------------------------------------------------
// Generador de cruces con BYEs
// ---------------------------------------------------------
export function generarCrucesConByes(
  clasificadosPorZona: number[], 
  totalClasificados: number,
  estrategiaByes: EstrategiaByes = "orden_zonas"
) {
  const cuadroFinal = nextPowerOf2(totalClasificados);
  const byes = cuadroFinal - totalClasificados;

  const clasificadosPrioridad: string[] = [];
  const maxNivel = Math.max(...clasificadosPorZona);
  
  if (estrategiaByes === "orden_zonas") {
    if (byes) {
      const numZonas = clasificadosPorZona.length;
    
      for (let nivel = 1; nivel <= maxNivel; nivel++) {
        const zonasConEsteNivel = clasificadosPorZona.filter(c => c >= nivel).length;

        if (zonasConEsteNivel === numZonas) {
          clasificadosPorZona.forEach((c, i) => {
            if (c >= nivel) {
              clasificadosPrioridad.push(`${nivel}º de zona ${i + 1}`);
            }
          });
        } else {
          for (let i = 0; i < zonasConEsteNivel; i++) {
            const posicion = i + 1;
            if (zonasConEsteNivel === 1) {
              clasificadosPrioridad.push(`Mejor ${nivel}º`);
            } else {
              clasificadosPrioridad.push(`${posicion}º mejor ${nivel}º`);
            }
          }
        }
      }

      const mejoresConBye = clasificadosPrioridad.slice(0, byes);
      clasificadosPrioridad.splice(0, byes);
      
      const restantes = clasificadosPrioridad;
      const mitad = Math.ceil(restantes.length / 2);
      const primeraMetad = restantes.slice(0, mitad);
      const segundaMetad = restantes.slice(mitad);

      const participantes: (string | null)[] = [...mejoresConBye];
      
      for (let i = 0; i < Math.max(primeraMetad.length, segundaMetad.length); i++) {
        if (i < primeraMetad.length) participantes.push(primeraMetad[i]);
        if (i < segundaMetad.length) participantes.push(segundaMetad[i]);
      }

      return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, byes);
    } else {
      for (let nivel = 1; nivel <= maxNivel; nivel++) {
        clasificadosPorZona.forEach((c, i) => {
          if (c >= nivel) clasificadosPrioridad.push(`${nivel}º mejor de zona ${i + 1}`);
        });
      }
      const participantes = clasificadosPrioridad;
      return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, 0);
    }
  }
  
  else if (estrategiaByes === "mejor_clasificacion") {
    for (let nivel = 1; nivel <= maxNivel; nivel++) {
      const clasificadosEsteNivel = clasificadosPorZona.filter(c => c >= nivel).length;
      
      for (let i = 0; i < clasificadosEsteNivel; i++) {
        const posicion = i + 1;
        if (clasificadosEsteNivel === 1) {
          clasificadosPrioridad.push(`Mejor ${nivel}º`);
        } else {
          clasificadosPrioridad.push(`${posicion}º mejor ${nivel}º`);
        }
      }
    }
    
    const participantes: (string | null)[] = [];
    
    if (byes > 0) {
      const mejoresConBye = clasificadosPrioridad.slice(0, byes);
      const restantes = clasificadosPrioridad.slice(byes);
      participantes.push(...mejoresConBye, ...restantes);
    } else {
      participantes.push(...clasificadosPrioridad);
    }
    
    return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, byes);
  }
  
  else {
    for (let nivel = 1; nivel <= maxNivel; nivel++) {
      clasificadosPorZona.forEach((c, i) => {
        if (c >= nivel) clasificadosPrioridad.push(`${nivel}º mejor de zona ${i + 1}`);
      });
    }
    const mejoresConBye = clasificadosPrioridad.slice(0, byes);
    const restantes = clasificadosPrioridad.slice(byes);
    const participantes = [...mejoresConBye, ...restantes];
    return generarEnfrentamientosDesdeParticipantes(participantes, cuadroFinal, byes);
  }
}

function generarEnfrentamientosDesdeParticipantes(
  participantes: (string | null)[], 
  cuadroFinal: number,
  byes: number
) {
  while (participantes.length < cuadroFinal) participantes.push(null);

  const enfrentamientos: Partido[] = [];
  for (let i = 0; i < cuadroFinal / 2; i++) {
    const local = participantes[i];
    const visitante = participantes[cuadroFinal - 1 - i];
    enfrentamientos.push({
      id: i + 1,
      ronda: 1,
      local,
      visitante,
      pasaAutomatico: local === null || visitante === null,
      detalles: {}
    });
  }

  const enfrentamientosConBye = enfrentamientos.filter(e => e.pasaAutomatico);
  const enfrentamientosSinBye = enfrentamientos.filter(e => !e.pasaAutomatico);
  
  const enfrentamientosReordenados: Partido[] = [];
  const mitadByes = Math.ceil(enfrentamientosConBye.length / 2);
  const byesPrimeraMetad = enfrentamientosConBye.slice(0, mitadByes);
  const byesSegundaMetad = enfrentamientosConBye.slice(mitadByes);
  
  const byesSerpenteados: Partido[] = [];
  for (let i = 0; i < Math.max(byesPrimeraMetad.length, byesSegundaMetad.length); i++) {
    if (i < byesPrimeraMetad.length) byesSerpenteados.push(byesPrimeraMetad[i]);
    if (i < byesSegundaMetad.length) byesSerpenteados.push(byesSegundaMetad[i]);
  }
  
  let idxByes = 0;
  let idxNormales = 0;
  
  for (let i = 0; i < enfrentamientos.length; i++) {
    if (idxByes < byesSerpenteados.length && 
        (i % 2 === 0 || idxNormales >= enfrentamientosSinBye.length)) {
      enfrentamientosReordenados.push(byesSerpenteados[idxByes]);
      idxByes++;
    } else if (idxNormales < enfrentamientosSinBye.length) {
      enfrentamientosReordenados.push(enfrentamientosSinBye[idxNormales]);
      idxNormales++;
    }
  }
  
  enfrentamientosReordenados.forEach((e, i) => e.id = i + 1);

  const identificadoresOrdenados: string[] = [];
  const participantesAntesNull = [...participantes].filter(p => p !== null) as string[];
  identificadoresOrdenados.push(...participantesAntesNull);
  
  return { 
    enfrentamientos: enfrentamientosReordenados, 
    byes,
    identificadoresOrdenados
  };
}

// ---------------------------------------------------------
// Avanzar ronda
// ---------------------------------------------------------
function avanzarRondaConIds(partidos: Partido[], idInicio: number): Partido[] {
  const ganadoresIds = partidos.map(p => p.id);
  const nuevaRonda: Partido[] = [];

  for (let i = 0; i < ganadoresIds.length; i += 2) {
    const local = ganadoresIds[i];
    const visitante = ganadoresIds[i + 1] || null;
    nuevaRonda.push({
      id: idInicio + (i / 2),
      ronda: (partidos[0]?.ronda ?? 1) + 1,
      local: null,
      visitante: null,
      pasaAutomatico: visitante === null,
      dePartido: [local, visitante!]
    });
  }

  return nuevaRonda;
}

// ---------------------------------------------------------
// Generador de eliminación directa
// ---------------------------------------------------------
export function generarEliminacionDirecta(
  total: number,
  deporte: Deporte = 'Tenis'
): ResultadoTorneo {
  const cuadroFinal = nextPowerOf2(total);
  const byes = cuadroFinal - total;
  const fases = Math.log2(cuadroFinal);

  const equiposIniciales = Array.from({ length: total }, (_, i) => ({
    id: i + 1,
    nombre: `Equipo ${i + 1}`,
  }));

  const participantes: (typeof equiposIniciales[0] | null)[] = [...equiposIniciales];
  while (participantes.length < cuadroFinal) {
    participantes.push(null);
  }

  const primeraRonda: Partido[] = [];
  for (let i = 0; i < cuadroFinal / 2; i++) {
    const local = participantes[i];
    const visitante = participantes[cuadroFinal - 1 - i];
    
    primeraRonda.push({
      id: i + 1,
      ronda: 1,
      local,
      visitante,
      pasaAutomatico: local === null || visitante === null,
      ganador: (local === null || visitante === null) 
        ? (local || visitante) 
        : undefined,
      detalles: {}
    });
  }

  const rondas: Ronda[] = [{ numero: 1, partidos: primeraRonda }];
  let rondaActual = primeraRonda;
  let idPartido = primeraRonda.length + 1;

  while (rondaActual.length > 1) {
    rondaActual = avanzarRondaConIds(rondaActual, idPartido);
    rondas.push({ numero: rondas.length + 1, partidos: rondaActual });
    idPartido += rondaActual.length;
  }

  return {
    formato: "eliminacion_directa",
    total,
    cuadroFinal,
    byes,
    fases,
    rondas,
    deporte
  };
}

// ---------------------------------------------------------
// Función principal
// ---------------------------------------------------------
export function generarTorneo(
  total: number,
  modalidad: "eliminacion_directa" | "zonas_y_eliminacion",
  clasificados?: number,
  zonasDeseadas?: number,
  estrategiaByes: EstrategiaByes = "orden_zonas",
  deporte: Deporte = 'Tenis'
): ResultadoTorneo | null {
  if (modalidad === "eliminacion_directa") {
    return generarEliminacionDirecta(total, deporte);
  }
  return generarZonasYEliminacion(
    total, 
    clasificados!, 
    zonasDeseadas,
    estrategiaByes,
    deporte
  );
}